// src/mazot-takip/src/pages/TankDuzeltme.js
import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  addDoc,
  serverTimestamp,
  Timestamp,
  orderBy,              // ⇦ liste sorgusu için eklendi
} from "firebase/firestore";

/* ---------------- Helpers ---------------- */
const ROLES = { VIEW:"view", ONLY_INPUT:"only_input", INPUT_APPROVE:"input_approve", CREATOR:"creator" };

const norm = (s="") => s
  .toString()
  .toUpperCase()
  .replaceAll("İ","I").replaceAll("ı","I")
  .replaceAll("Ş","S").replaceAll("ş","S")
  .replaceAll("Ğ","G").replaceAll("ğ","G")
  .replaceAll("Ü","U").replaceAll("ü","U")
  .replaceAll("Ö","O").replaceAll("ö","O")
  .replaceAll("Ç","C").replaceAll("ç","C")
  .trim();

const parseNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const fmt = (n) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(Number(n||0));

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,"0");
  const day = String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${day}`;
}
function firstOfPrevMonthStr(){
  const d=new Date();
  d.setDate(1);              // bu ayın ilki
  d.setMonth(d.getMonth()-1);// geçen ay
  const y=d.getFullYear();
  const m=String(d.getMonth()+1).padStart(2,"0");
  return `${y}-${m}-01`;
}

/* ---------------- Data fetchers ---------------- */
async function fetchMazotRole(email) {
  try {
    if (!email) return ROLES.VIEW;
    // users/{email} veya email eşleşmesi
    const snap1 = await getDoc(doc(db, "users", email));
    let data = snap1.exists() ? snap1.data() : null;
    if (!data) {
      const s2 = await getDocs(query(collection(db, "users"), where("email","==", email)));
      if (!s2.empty) data = s2.docs[0].data();
    }
    const m = data?.["mazot-takip"] || data?.["mazot_takip"] || {};
    if (m.olusturucu) return ROLES.CREATOR;
    if (m.giren_onaylayan) return ROLES.INPUT_APPROVE;
    if (m.giren) return ROLES.ONLY_INPUT;
    if (m.izleyen) return ROLES.VIEW;
    return ROLES.VIEW;
  } catch {
    return ROLES.VIEW;
  }
}

async function fetchTesisOptions() {
  const out = new Set();
  try {
    const s1 = await getDocs(collection(db, "tesisler"));
    s1.docs.forEach((d) => {
      const name = d.data()?.name || d.data()?.ad || d.data()?.title;
      if (name) out.add(name);
    });
  } catch {}
  try {
    const s2 = await getDocs(collection(db, "fuel_tanks"));
    s2.docs.forEach((d) => {
      const t = d.data()?.tesis;
      if (t) out.add(t);
    });
  } catch {}
  return Array.from(out).sort((a,b)=>a.localeCompare(b,"tr"));
}

async function fetchTankMapByTesis() {
  const m = new Map();
  const snap = await getDocs(collection(db, "fuel_tanks"));
  snap.docs
    .map((d)=>({id:d.id, ...(d.data()||{})}))
    .filter((x)=>x.active !== false)
    .forEach((t)=>{
      const key = norm(t.tesis||"");
      if (!m.has(key)) m.set(key, []);
      m.get(key).push({ id:t.id, name:t.name || t.tankAdi || "TANK", tesis: t.tesis || "" });
    });
  return m;
}

// Stok: Tesisteki tankın hareket toplamı = İkmal(+), Araç Dolum(–), Düzeltme(±)
async function computeRecordedStock(tankId) {
  if (!tankId) return 0;

  let deliveries = 0;
  try {
    const s = await getDocs(query(collection(db, "fuel_deliveries"), where("tankId","==", tankId)));
    s.docs.forEach(d => {
      const v = d.data()?.liters;
      if (Number.isFinite(v)) deliveries += Number(v);
    });
  } catch {}

  let issues = 0;
  try {
    const s = await getDocs(query(collection(db, "fuel_transactions"), where("tankId","==", tankId)));
    s.docs.forEach(d => {
      const row = d.data()||{};
      if (row.deleted === true) return; // soft silineni sayma
      const v = row.liters;
      if (Number.isFinite(v)) issues += Number(v);
    });
  } catch {}

  let adjustments = 0;
  try {
    const s = await getDocs(query(collection(db, "fuel_tank_adjustments"), where("tankId","==", tankId)));
    s.docs.forEach(d => {
      const v = d.data()?.deltaLiters;
      if (Number.isFinite(v)) adjustments += Number(v);
    });
  } catch {}

  // ikmal(+) - dolum(–) + düzeltme(±)
  return deliveries - issues + adjustments;
}

/* ---------------- Component ---------------- */
export default function TankDuzeltme() {
  const [role, setRole] = useState(null);

  // seçimler
  const [tesis, setTesis] = useState("");
  const [tankId, setTankId] = useState(null);
  const [tankName, setTankName] = useState("");

  // veriler
  const [tesisOptions, setTesisOptions] = useState([]);
  const [tankMap, setTankMap] = useState(new Map());
  const [recordedStock, setRecordedStock] = useState(0);

  // form
  const [date, setDate] = useState(todayStr());
  const [measured, setMeasured] = useState("");  // ölçülen stok (lt) – OPSIYONEL (şimdilik pasif)
  const [delta, setDelta] = useState("");        // fark (± lt)
  const [note, setNote] = useState("");

  // liste filtreleri
  const [fStart, setFStart] = useState(firstOfPrevMonthStr()); // default: geçen ay 1'i
  const [fEnd, setFEnd] = useState(todayStr());                // default: bugün
  const [fTesis, setFTesis] = useState("ALL");                 // default: tüm tesisler
  const [listRows, setListRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  useEffect(() => {
    fetchMazotRole(auth.currentUser?.email).then(setRole);
    Promise.all([fetchTesisOptions(), fetchTankMapByTesis()]).then(([tos, tmap])=>{
      setTesisOptions(tos);
      setTankMap(tmap);
    });
  }, []);

  // tesis seçilince tankı bağla ve stok hesapla
  useEffect(() => {
    const arr = tankMap.get(norm(tesis)) || [];
    if (arr.length === 0) {
      setTankId(null);
      setTankName("");
      setRecordedStock(0);
      return;
    }
    // tek tank varsayımı; birden fazlaysa ilkini al
    setTankId(arr[0].id);
    setTankName(arr[0].name || "TANK");
  }, [tesis, tankMap]);

  // tankId belirlendiyse stok hesapla
  useEffect(() => {
    if (!tankId) return;
    computeRecordedStock(tankId).then(setRecordedStock);
  }, [tankId]);

  // Daha esnek giriş: nokta yasak; varsa komple kaldır.
  // Virgül küsurat için serbest. +/− ve rakamlar serbest. Harf/diğer yok sayılır.
  const onDeltaChange = (raw) => {
    const v = String(raw).replace(/\./g, ""); // "1.000" -> "1000", "." tamamen yasak
    if (v && /[^0-9+\-,]/.test(v)) return;   // yalnızca 0-9 + - , izinli

    setDelta(v);

    // Sayıya çevrilebiliyorsa ölçülen stoğu hesapla ve pasif alana yaz
    const d = parseNum(v); // virgülü noktaya çevirerek parse eder
    if (d !== null) {
      const calcMeasured = Number(recordedStock || 0) + d;
      setMeasured(String(calcMeasured));
    } else {
      setMeasured("");
    }
  };

  const canAdjust = [ROLES.INPUT_APPROVE, ROLES.CREATOR].includes(role);

  const handleSave = async () => {
    if (!canAdjust) return alert("Yetkiniz yok.");
    if (!tesis) return alert("Tesis seçiniz.");
    if (!tankId) return alert("Bu tesis için kayıtlı tank bulunamadı.");
    if (!date) return alert("Tarih zorunlu.");

    const d = parseNum(delta);
    if (d === null || d === 0) return alert("Fark (± lt) zorunlu ve 0 olamaz.");

    const measuredNum = parseNum(measured); // opsiyonel (alan pasif ama yine de kayda geçsin)

    const payload = {
      date: Timestamp.fromDate(new Date(`${date}T12:00:00`)),
      tesis,
      tankId,
      tankName: tankName || null,
      recordedStockAtSave: Number(recordedStock || 0),
      deltaLiters: d,                     // ±
      measuredStockLiters: measuredNum,   // opsiyonel
      note: note || null,
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser?.email || "anonim",
    };

    await addDoc(collection(db, "fuel_tank_adjustments"), payload);

    alert("Düzeltme kaydedildi ✅");
    // formu hafif temizle, stok güncelle
    setNote("");
    setMeasured("");
    setDelta("");
    if (tankId) computeRecordedStock(tankId).then(setRecordedStock);
    // listeyi de tazele (kullanıcı aralığı uygunsa)
    loadList(); 
  };

  // --- Liste çekici (tarih aralığı + opsiyonel tesis filtresi) ---
  async function loadList(){
    setListLoading(true);
    try{
      const start = new Date(`${fStart}T00:00:00`);
      const end   = new Date(`${fEnd}T23:59:59`);
      const tsStart = Timestamp.fromDate(start);
      const tsEnd   = Timestamp.fromDate(end);

      let docs = [];
      try{
        // Tarih aralığıyla çek, en yeni üstte
        const q1 = query(
          collection(db, "fuel_tank_adjustments"),
          where("date", ">=", tsStart),
          where("date", "<=", tsEnd),
          orderBy("date", "desc")
        );
        const s1 = await getDocs(q1);
        docs = s1.docs;
      }catch{
        // Index sorunu olursa geniş çek ve client-side filtrele
        const sAll = await getDocs(collection(db, "fuel_tank_adjustments"));
        docs = sAll.docs.filter(d=>{
          const x=d.data()||{};
          const ms = x.date?.toDate?.()?.getTime?.() ?? new Date(x.date||0).getTime();
          return ms>=start.getTime() && ms<=end.getTime();
        });
      }

      let arr = docs.map(d=>({id:d.id, ...(d.data()||{})}));
      if (fTesis !== "ALL"){
        arr = arr.filter(r => (r.tesis||"") === fTesis);
      }
      // tarih desc sırala
      arr.sort((a,b)=>{
        const am = a.date?.toDate?.()?.getTime?.() ?? new Date(a.date||0).getTime();
        const bm = b.date?.toDate?.()?.getTime?.() ?? new Date(b.date||0).getTime();
        return bm - am;
      });
      setListRows(arr);
    }finally{
      setListLoading(false);
    }
  }

  // filtreler değiştikçe listeyi yükle
  useEffect(()=>{ loadList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */}, [fStart, fEnd, fTesis]);

  /* ---------------- UI ---------------- */
  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ textAlign: "left", color: "#334155", fontSize: 22, fontWeight: "bold", marginBottom: 16 }}>
        Tank Sıfırlama / Düzeltme
      </h2>

      {/* Seçimler */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12, marginBottom: 12 }}>
        <div>
          <label className="block text-sm">Tesis</label>
          <input
            list="dl-tesis"
            value={tesis}
            onChange={(e)=>setTesis(e.target.value)}
            placeholder="Tesis seçin"
            style={{ width:"100%", border:"1px solid #cbd5e1", padding:8, borderRadius:6 }}
          />
          <datalist id="dl-tesis">
            {tesisOptions.map((t)=><option key={t} value={t} />)}
          </datalist>
        </div>

        <div>
          <label className="block text-sm">Tank</label>
          <input
            value={tankName || (tankId ? "TANK" : "")}
            readOnly
            placeholder="Tesis seçince otomatik gelir"
            style={{ width:"100%", border:"1px dashed #cbd5e1", padding:8, borderRadius:6, background:"#f8fafc" }}
          />
        </div>

        <div>
          <label className="block text-sm">Tarih</label>
          <input
            type="date"
            value={date}
            onChange={(e)=>setDate(e.target.value)}
            style={{ width:"100%", border:"1px solid #cbd5e1", padding:8, borderRadius:6 }}
          />
        </div>
      </div>

      {/* Bilgi kutuları */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginBottom: 12 }}>
        <div style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:12 }}>
          <div style={{ fontSize:12, color:"#64748b" }}>Kayıtlara göre stok</div>
          <div style={{ fontSize:22, fontWeight:"bold" }}>{fmt(recordedStock)} lt</div>
        </div>
        <div style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:12, background:"#f8fafc" }}>
          <div style={{ fontSize:12, color:"#64748b" }}>Ölçülen stok (lt) – opsiyonel</div>
          <input
            value={measured}
            onChange={()=>{}}
            placeholder="Otomatik hesaplanır"
            disabled
            style={{ width:"100%", border:"1px dashed #cbd5e1", padding:8, borderRadius:6, marginTop:6, background:"#f1f5f9", color:"#64748b", cursor:"not-allowed" }}
          />
        </div>
        <div style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:12 }}>
          <div style={{ fontSize:12, color:"#64748b" }}>Fark (± lt)</div>
          <input
            value={delta}
            onChange={(e)=>onDeltaChange(e.target.value)}
            placeholder="Örn: -100,5 veya 25"
            inputMode="decimal"
            style={{ width:"100%", border:"1px solid #cbd5e1", padding:8, borderRadius:6, marginTop:6 }}
          />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="block text-sm">Not (opsiyonel)</label>
        <textarea
          value={note}
          onChange={(e)=>setNote(e.target.value)}
          rows={3}
          placeholder="Örn: Seviye çubuğu kalibre farkı, drenaj yapıldı vb."
          style={{ width:"100%", border:"1px solid #cbd5e1", padding:8, borderRadius:6 }}
        />
      </div>

      <div>
        <button
          onClick={handleSave}
          disabled={!canAdjust}
          style={{
            background: canAdjust ? "#4f46e5" : "#94a3b8",
            color:"#fff",
            padding:"10px 16px",
            border:"none",
            borderRadius:6,
            fontWeight:"bold",
            cursor: canAdjust ? "pointer" : "not-allowed"
          }}
        >
          Düzeltmeyi Kaydet
        </button>
      </div>

      {!canAdjust && (
        <div style={{ marginTop:12, fontSize:12, color:"#64748b" }}>
          Not: Düzeltme yetkisi yalnızca <b>giren_onaylayan</b> ve <b>olusturucu</b> rollerindedir.
        </div>
      )}

      {/* ====================== LİSTE BÖLÜMÜ ====================== */}
      <hr style={{ margin:"24px 0", border:"0", borderTop:"1px solid #e5e7eb" }} />

      <h3 style={{ fontSize:18, fontWeight:"bold", color:"#334155", marginBottom:10 }}>Sıfırlama Kayıtları</h3>

      {/* Filtreler */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:10, marginBottom:12 }}>
        <div>
          <label className="block text-sm">Başlangıç</label>
          <input
            type="date"
            value={fStart}
            onChange={(e)=>setFStart(e.target.value)}
            style={{ width:"100%", border:"1px solid #cbd5e1", padding:8, borderRadius:6 }}
          />
        </div>
        <div>
          <label className="block text-sm">Bitiş</label>
          <input
            type="date"
            value={fEnd}
            onChange={(e)=>setFEnd(e.target.value)}
            style={{ width:"100%", border:"1px solid #cbd5e1", padding:8, borderRadius:6 }}
          />
        </div>
        <div>
          <label className="block text-sm">Tesis</label>
          <select
            value={fTesis}
            onChange={(e)=>setFTesis(e.target.value)}
            style={{ width:"100%", border:"1px solid #cbd5e1", padding:8, borderRadius:6, background:"#fff" }}
          >
            <option value="ALL">Tümü</option>
            {tesisOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* Liste Tablosu */}
      <div style={{ overflowX:"auto" }}>
        <table style={{ borderCollapse:"collapse", width:"100%", minWidth:"980px", fontSize:"13px" }}>
          <thead style={{ background:"#eef2f7" }}>
            <tr>
              {["Tarih","Tesis","Tank","Sıfırlayan","Önceki (lt)","Sonraki (lt)","Sıfırlama (± lt)","Açıklama"].map((h,i)=>(
                <th key={i} style={{ border:"1px solid #e5e7eb", padding:"8px", textAlign:"left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {listLoading ? (
              <tr><td colSpan={8} style={{ padding:12, textAlign:"center", color:"#64748b" }}>Yükleniyor…</td></tr>
            ) : listRows.length===0 ? (
              <tr><td colSpan={8} style={{ padding:12, textAlign:"center", color:"#64748b" }}>Kayıt bulunamadı</td></tr>
            ) : (
              listRows.map((r,idx)=>{
                const dt = r.date?.toDate?.() || new Date(r.date||0);
                const prev = Number(r.recordedStockAtSave || 0);
                const dlt  = Number(r.deltaLiters || 0);
                const next = prev + dlt;
                return (
                  <tr key={r.id || idx}>
                    <td style={{ border:"1px solid #e5e7eb", padding:"6px" }}>
                      {isNaN(dt.getTime()) ? "-" : dt.toLocaleDateString("tr-TR")}
                    </td>
                    <td style={{ border:"1px solid #e5e7eb", padding:"6px" }}>{r.tesis || "-"}</td>
                    <td style={{ border:"1px solid #e5e7eb", padding:"6px" }}>{r.tankName || "-"}</td>
                    <td style={{ border:"1px solid #e5e7eb", padding:"6px" }}>{r.createdBy || "-"}</td>
                    <td style={{ border:"1px solid #e5e7eb", padding:"6px", textAlign:"right" }}>{fmt(prev)}</td>
                    <td style={{ border:"1px solid #e5e7eb", padding:"6px", textAlign:"right" }}>{fmt(next)}</td>
                    <td style={{ border:"1px solid #e5e7eb", padding:"6px", textAlign:"right" }}>{fmt(dlt)}</td>
                    <td style={{ border:"1px solid #e5e7eb", padding:"6px" }}>{r.note || "-"}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
